import { colors } from '@/styles';
import React, { forwardRef } from 'react';
import LassoControl from '../Mapbox/Controls/Lasso';
import ZoomControl from '../Mapbox/Controls/Zoom';
import RadiusControl from '../Mapbox/Controls/Radius';
import Layer from '../Mapbox/Layer';
import Map from '../Mapbox/Map';
import Source from '../Mapbox/Source';

const DEFAULT_CENTER = [-104.99, 39.74];

const SubregionMap = forwardRef(
    (
        {
            node,
            subregions,
            callbacks,
            highlightOverride,
            selectedSubregion,
            hoveredSubregion,
            selectedZips,
            hoveredSubregionZip,
            radiusControl,
        },
        ref
    ) => (
        <Map ref={ref} node={node} center={DEFAULT_CENTER} zoom={7} minZoom={5} callbacks={callbacks}>
            {({ map }) => {
                return (
                    <>
                        <Source map={map} url="mapbox://kevinmwalsh.zcta-2020" name="zcta.2020" />
                        <ZoomControl map={map} location="bottom-left" />
                        {radiusControl ? (
                            <RadiusControl map={map} callbacks={radiusControl.callbacks} />
                        ) : null}
                        <LassoControl
                            map={map}
                            callbacks={{
                                onClear: () => {
                                    callbacks.selectObject((prev) => {
                                        return {
                                            ...prev,
                                            zip: {},
                                        };
                                    });
                                },
                                onSelect: (start, end) => {
                                    const features = map.queryRenderedFeatures([start, end], {
                                        layers: ['outlines-layer'],
                                    });

                                    callbacks.selectObject((prev) => {
                                        const next = features.reduce(
                                            (acc, feature) => {
                                                acc[feature.properties.NAME20] = acc[feature.properties.NAME20]
                                                    ? !acc[feature.properties.NAME20]
                                                    : true;

                                                return acc;
                                            },
                                            { ...prev.zip }
                                        );

                                        return {
                                            ...prev,
                                            zip: next,
                                        };
                                    });
                                },
                            }}
                        />
                        <Layer
                            id="outlines-layer"
                            map={map}
                            source="zcta.2020"
                            source-layer="zctas"
                            type="fill"
                            paint={{
                                'fill-color': 'rgba(0,0,0,0)',
                                'fill-outline-color': '#000',
                            }}
                        />
                        {(subregions || []).map((subregion) => (
                            <Layer
                                id={`subregion-layer-${subregion.subregion_id}`}
                                key={subregion.subregion_id}
                                map={map}
                                source="zcta.2020"
                                source-layer="zctas"
                                type="fill"
                                paint={{
                                    'fill-color': highlightOverride
                                        ? callbacks.genColor(subregion || {}) || 'rgba(0,0,0,0)'
                                        : '#4C4C4C',
                                    'fill-opacity': highlightOverride ? 0.7 : 0.3,
                                    'fill-outline-color': '#000',
                                }}
                                filter={['in', 'NAME20', ...subregion.zips.map((zip) => zip.zip)]}
                            />
                        ))}
                        <Layer
                            id="selected-subregion-layer"
                            map={map}
                            source="zcta.2020"
                            source-layer="zctas"
                            type="fill"
                            paint={{
                                'fill-color': callbacks.genColor(selectedSubregion || {}) || 'rgba(0,0,0,0)',
                                'fill-opacity': 0.7,
                                'fill-outline-color': '#000',
                            }}
                            filter={['in', 'NAME20', ...(selectedSubregion?.zips.map((o) => o.zip) || [])]}
                        />
                        <Layer
                            id="hover-subregion-layer"
                            map={map}
                            source="zcta.2020"
                            source-layer="zctas"
                            type="fill"
                            paint={{
                                'fill-color': callbacks.genColor(hoveredSubregion || {}) || 'rgba(0,0,0,0)',
                                'fill-opacity': 0.7,
                                'fill-outline-color': '#000',
                            }}
                            filter={['in', 'NAME20', ...(hoveredSubregion?.zips.map((o) => o.zip) || [])]}
                        />
                        <Layer
                            id="hover-zip-layer"
                            map={map}
                            source="zcta.2020"
                            source-layer="zctas"
                            type="fill"
                            paint={{
                                'fill-color': '#000',
                                'fill-opacity': 0.2,
                                'fill-outline-color': '#000',
                            }}
                            filter={['in', 'NAME20', ...(hoveredSubregionZip ? [hoveredSubregionZip] : [])]}
                        />
                        <Layer
                            id="selected-zip-layer"
                            map={map}
                            source="zcta.2020"
                            source-layer="zctas"
                            type="fill"
                            paint={{
                                'fill-color': colors.greens.primary,
                                'fill-opacity': 0.7,
                                'fill-outline-color': '#000',
                            }}
                            filter={['in', 'NAME20', ...selectedZips]}
                        />
                    </>
                );
            }}
        </Map>
    )
);

export default SubregionMap;
